import { STATUS, TICKET_WORKFLOW_STATUS } from '../../../common/constants';
import { uploadOfflineImageAttachments, filterRemoteUrls } from '../uploadAttachments';
import { deepCopy } from '../utils';

const isRemoteUrl = url => typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'));

/**
 * Build an offline ticket object that can be used for optimistic updates.
 * This creates a minimal ticket representation that matches the GraphQL schema.
 */
const buildOfflineTicket = ({ input, queueEntry }) => {
	const ticketId = queueEntry.id;
	const visibleToDrivers = input.visibleToDrivers === true;

	return {
		__typename: 'Ticket',
		id: ticketId,
		description: input.description || '',
		status: STATUS.OPEN || 'OPEN',
		ticketWorkflowStatus: visibleToDrivers ? TICKET_WORKFLOW_STATUS.ACTIVE : TICKET_WORKFLOW_STATUS.INCOMING,
		priority: input.priority,
		type: input.type,
		visibleToDrivers,
		imageUrls: input.imageUrls || [],
		machine: input.machine ? {
			__typename: 'Machine',
			id: input.machine,
		} : null,
		docketId: input.docketId,
		offlineQueuedAt: queueEntry.createdAt,
		offline: true,
	};
};

/**
 * Offline operation config for createTicket mutation.
 *
 * Handles:
 * - Queuing ticket creation when offline
 * - Storing local image URIs as attachments
 * - Uploading images when back online before creating ticket
 * - Providing optimistic ticket object for immediate UI feedback
 */
const createTicketConfig = {
	queueWhenAttachments: true,
	// Deep copy variables for safe serialization to AsyncStorage
	serializeVariables: ({ variables }) => {
		return deepCopy(variables);
	},
	createMeta: async ({ variables, helpers }) => {
		const input = variables?.input || {};
		const serverEndpoint = helpers?.getServerEndpoint ? await helpers.getServerEndpoint() : null;
		return {
			docketId: input.docketId || null,
			machineId: input.machine || null,
			formInput: deepCopy(input),
			type: 'createTicket',
			serverEndpoint,
			s3Path: 'images/machines/',
			localImageUrls: (input.imageUrls || []).filter(url => !isRemoteUrl(url)),
		};
	},
	extractAttachments: ({ variables }) => {
		const input = variables?.input || {};
		return (input.imageUrls || [])
			.filter(url => !isRemoteUrl(url))
			.map((url) => ({
				type: 'image',
				localUri: url,
			}));
	},
	prepareVariables: async ({ entry }) => {
		const { variables } = entry;
		const clonedVariables = deepCopy(variables);
		const input = clonedVariables.input || {};
		const attachments = entry.attachments || [];

		if (!attachments.length) {
			input.imageUrls = filterRemoteUrls(input.imageUrls);
			return {
				...clonedVariables,
				input,
			};
		}

		const uploaded = await uploadOfflineImageAttachments({ attachments, meta: entry.meta });
		const existingRemote = filterRemoteUrls(input.imageUrls);
		const uploadedUrls = uploaded.map(file => file.remoteUrl);

		input.imageUrls = [...existingRemote, ...uploadedUrls];

		return {
			...clonedVariables,
			input,
		};
	},
	getOfflineResult: ({ variables, queueEntry }) => {
		const input = variables?.input || {};

		return {
			createTicket: buildOfflineTicket({ input, queueEntry }),
		};
	},
};

export default createTicketConfig;
